import { useProviderFromContext } from "@@/providers/state/contexts/use-provider-from-context";
import { ResourcePicker } from "@@/resources/picker/resource-picker";
import { useResourcesForProvider } from "@@/resources/resources/use-resources";
import { FlexColumn } from "@@/shared/flex-containers";
import type { FormId } from "@@/shared/form/form-and-field-id";
import { useFormId } from "@@/shared/form/form-id.context";
import type { AppTheme } from "@@/styles/theme";
import type { Interpolation } from "@emotion/react";
import type {
    DeepKeysOf,
    DeepPathValueType,
    ProviderId,
    RequiredTranslatable,
    Resource,
    ResourceGlobal,
    ResourceId,
} from "@towni/common";
import { emptyArrayOf, translation } from "@towni/common";
import { useMemo } from "react"; // Import useMemo
import type { ZodSchema } from "zod";
import { useForm3Field } from "./form3.use-form-field"; // Assuming this is the correct path for Form3 field hook

// Define the expected value type for this picker
type ValueType = ResourceId[];

// Define Props, similar to Form2 but adapted for Form3
type Props<FieldId> = {
    readonly className?: string;
    readonly fieldId: FieldId;
    readonly formId?: FormId;
    readonly spin?: boolean; // Allow overriding the loading state indicator
    readonly providerId?: ProviderId; // Allow overriding the provider context

    // Css properties
    readonly containerCss?: Interpolation<AppTheme>;

    // Resource fetching/filtering options
    readonly resources?: ResourceGlobal[]; // Allow passing in resources directly
    readonly hideResourceIds?: ResourceId[]; // Resources to exclude
    readonly filter?: (resource: Resource) => boolean; // Custom filtering logic

    // UI Props for ResourcePicker
    readonly title?: RequiredTranslatable; // Optional title for the picker section
};

const createForm3ResourcePickerMultipleComponent = <
    FormSchema extends ZodSchema,
    FormData extends Record<string, unknown>,
>() => {
    const Form3ResourcePickerMultiple = <
        FieldId extends DeepKeysOf<FormData> = DeepKeysOf<FormData>,
        // Ensure FieldValue matches the expected type for this component (ResourceId[])
        // The useForm3Field hook will handle ensuring this type matches the schema type
        FieldValue extends ValueType = ValueType,
    >(
        props: Props<FieldId>,
    ) => {
        // 1. Get Provider ID (from prop or context)
        const provider = useProviderFromContext();
        const providerId = props.providerId || provider._id;
        if (!providerId) {
            // This check remains important
            throw new Error(
                "ProviderId is required to fetch resources for ResourcePicker",
            );
        }

        // 2. Get Form ID (from prop or context)
        const formIdFromContext = useFormId({ doNotThrow: true });
        const formId = props.formId || formIdFromContext;

        // 3. Connect to Form3 state using useForm3Field
        const field = useForm3Field<FormSchema, FormData, FieldId, FieldValue>({
            fieldId: props.fieldId,
            formId: formId, // Pass formId if your hook uses it
        });

        // Handle case where field might not be found (good practice)
        if (!field) {
            throw new Error(
                `Form3 field '${String(props.fieldId)}' not found within form '${formId}'. Ensure it's registered correctly.`,
            );
        }

        // 4. Fetch Resources (same logic as Form2)
        // The query key might need adjustment if you want form-specific caching, but typically provider-based is fine.
        const [__, resourceQuery] = useResourcesForProvider(providerId);
        const isLoading = resourceQuery.isPending || !!props.spin;

        // 5. Prepare the list of resources to select from (same logic as Form2)
        const resourcesToPickFrom = useMemo(() => {
            const availableResources =
                props.resources ?? // Use provided resources if available
                resourceQuery.data ?? // Otherwise use fetched data
                emptyArrayOf<ResourceGlobal>(); // Default to empty array

            // Apply filters
            return availableResources
                .filter(resource =>
                    // Filter out hidden resources
                    props.hideResourceIds
                        ? !props.hideResourceIds.includes(resource._id)
                        : true,
                )
                .filter(
                    resource =>
                        // Apply custom filter function if provided
                        !props.filter || props.filter(resource),
                );
        }, [props, resourceQuery.data]); // Dependencies for memoization

        // 6. Render the Picker component
        return (
            <FlexColumn
                className={props.className}
                // Use String() for fieldId in key for robustness if it's not a primitive
                key={`${formId}_${String(props.fieldId)}`}
                fillParentWidth // Keep styling from Form2 example
                css={props.containerCss} // Apply custom container styles
            >
                <ResourcePicker
                    key={isLoading ? "loading" : "ready"}
                    title={
                        props.title ??
                        translation({
                            sv: "Välj resurser",
                            en: "Choose resources",
                        })
                    }
                    spin={isLoading} // Pass the loading state
                    mode="MULTI" // Hardcoded as this is the MULTI picker
                    initiallySelected={new Set(field.value ?? [])}
                    resources={resourcesToPickFrom} // Pass the filtered list
                    onChange={newValues => {
                        field.setValue(
                            newValues as DeepPathValueType<FormData, FieldId>,
                        );
                        // Mark the field as touched
                        // No need for manual validationTrigger() or field.setTouched(true).
                    }}
                />
            </FlexColumn>
        );
    };

    // Return the specific component tied to the FormSchema/FormData
    return Form3ResourcePickerMultiple;
};

export { createForm3ResourcePickerMultipleComponent };
